import Vue from "vue";

const TOOLTIP_SELECTOR = "[data-tooltip], [aria-label], [title], button, .icon-button";
const NATIVE_TITLE_ATTR = "data-native-title";

function createTooltipElement(): HTMLDivElement {
  const element = document.createElement("div");
  element.className = "app-tooltip";
  document.body.appendChild(element);
  return element;
}

function ensureTooltipStyles() {
  if (document.getElementById("app-tooltip-style")) {
    return;
  }

  const style = document.createElement("style");
  style.id = "app-tooltip-style";
  style.textContent = `
    .app-tooltip {
      position: fixed;
      z-index: 10000;
      max-width: 260px;
      padding: 7px 10px;
      border-radius: 3px;
      background: #242424;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
      color: #ffffff;
      font-family: 'Proxima Nova', sans-serif;
      font-size: 13px;
      line-height: 16px;
      opacity: 0;
      pointer-events: none;
      transform: translateY(2px);
      transition: opacity 80ms ease, transform 80ms ease;
      white-space: nowrap;
    }

    .app-tooltip.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  `;
  document.head.appendChild(style);
}

function getTextFromElement(element: HTMLElement): string {
  const explicitText =
    element.getAttribute("data-tooltip") ||
    element.getAttribute("aria-label") ||
    element.getAttribute("title");

  if (explicitText) {
    return explicitText.trim();
  }

  if (element.tagName.toLowerCase() === "button") {
    return (element.textContent || "").trim();
  }

  return "";
}

function getTooltipTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  const tooltipTarget = target.closest(TOOLTIP_SELECTOR) as HTMLElement | null;
  if (!tooltipTarget || tooltipTarget.closest("[data-tooltip-disabled='true']")) {
    return null;
  }

  return tooltipTarget;
}

function preserveNativeTitle(element: HTMLElement) {
  const title = element.getAttribute("title");
  if (!title) {
    return;
  }

  element.setAttribute(NATIVE_TITLE_ATTR, title);
  element.removeAttribute("title");
}

function restoreNativeTitle(element: HTMLElement | null) {
  if (!element) {
    return;
  }

  const title = element.getAttribute(NATIVE_TITLE_ATTR);
  if (!title) {
    return;
  }

  element.setAttribute("title", title);
  element.removeAttribute(NATIVE_TITLE_ATTR);
}

function positionTooltip(tooltip: HTMLElement, target: HTMLElement) {
  const targetRect = target.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const viewportPadding = 8;
  const gap = 8;

  let top = targetRect.top - tooltipRect.height - gap;
  if (top < viewportPadding) {
    top = targetRect.bottom + gap;
  }

  let left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
  left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));

  tooltip.style.top = `${top}px`;
  tooltip.style.left = `${left}px`;
}

const tooltipPlugin = {
  install(_vue: typeof Vue) {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    ensureTooltipStyles();

    let tooltip: HTMLDivElement | null = null;
    let activeTarget: HTMLElement | null = null;

    const show = (event: Event) => {
      const target = getTooltipTarget(event.target);
      if (!target) {
        return;
      }

      const text = getTextFromElement(target);
      if (!text) {
        return;
      }

      preserveNativeTitle(target);
      activeTarget = target;
      tooltip = tooltip || createTooltipElement();
      tooltip.textContent = text;
      tooltip.classList.add("is-visible");
      positionTooltip(tooltip, target);
    };

    const hide = (event?: Event) => {
      if (event instanceof MouseEvent && activeTarget && event.relatedTarget instanceof Node) {
        if (activeTarget.contains(event.relatedTarget)) {
          return;
        }
      }

      if (tooltip) {
        tooltip.classList.remove("is-visible");
      }

      restoreNativeTitle(activeTarget);
      activeTarget = null;
    };

    const reposition = () => {
      if (tooltip && activeTarget) {
        positionTooltip(tooltip, activeTarget);
      }
    };

    document.addEventListener("mouseover", show);
    document.addEventListener("focusin", show);
    document.addEventListener("mouseout", hide);
    document.addEventListener("focusout", hide);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
  },
};

export default tooltipPlugin;
